import React, { useEffect, useState } from "react";
import CourseHeader from "../../components/customCoursework/CourseHeader/CourseHeader";
import { NavigationProp, useNavigation, useRoute } from "@react-navigation/native";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { useActiveCourse } from "../../contexts/ActiveCourseContext";
import { StyleSheet, Text, View } from "react-native";

const studentRoutes = [
  "Custom Course Student",
  "Custom Assignment Student",
  "Custom Resource Student",
  "Custom Course AITutor",
  "Custom Course Assessment",
];

const formatDate = (isoDateString: string) => {
  if (!isoDateString) return "";

  const date = new Date(isoDateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
};

const CourseHeaderContainer = ({ isEditor = false }) => {
  const [title, setTitle] = useState("");
  const [label, setLabel] = useState("");
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute();
  const params = route.params as Record<string, string | boolean | undefined>;
  const routeName = route.name;

  const {
    courseData,
    courseId,
    handleOpenGenerateDialog,
    handleTogglePublishDialog,
    isSubmitting,
    assignmentData,
    loadingAssignment,
    setActiveResource,
    userSubmissionsData,
    loadingUserSubmissions,
    axioResourceData,
  } = useActiveCourse();

  const { courseId: courseIdQueryParam1, course_id: courseIdQueryParam } = params || {};

  const courseIdString =
    typeof courseIdQueryParam1 === "string"
      ? courseIdQueryParam1
      : typeof courseIdQueryParam === "string"
        ? courseIdQueryParam
        : courseId;

  const handleExitPress = () => {
    setActiveResource(null);

    const isStudentRoute = studentRoutes.includes(routeName);

    if (isStudentRoute) {
      if (params?.module_id) {
        navigation.navigate("Custom Module Student", {
          module_id: typeof params.module_id === "string" ? params.module_id : "",
          course_id: courseIdString,
          integration_id: typeof params.integration_id === "string" ? params.integration_id : "",
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: courseIdString,
          integration_id: typeof params.integration_id === "string" ? params.integration_id : "",
        });
      }
    } else {
      navigation.navigate("Course Information", {
        courseId: courseIdString,
        courseName: courseData?.getCourseModulesActivitiesLite?.data?.[0]?.name || "Unknown Course",
        returnTo: "Manage Courses",
        activeTab: typeof params?.activeTab === "string" ? params.activeTab : undefined,
      });
    }
  };

  const handleOpenCourseSettings = () => {
    navigation.navigate("Course Settings", { courseId, tab: "courseInfo" });
  };

  const handleNavigateToStudentView = () => {
    navigation.navigate("Custom Course Student", {
      course_id: courseIdString,
    });
  };

  const course = courseData?.getCourseModulesActivitiesLite?.data?.[0];
  const isDraft = course?.isDraft;
  const moduleCount = course?.modules?.length ?? 0;
  const showRightSection = routeName === "Custom Coursework Modules";

  useEffect(() => {
    if (routeName === "Custom Assignment Student") {
      setTitle(assignmentData?.getAssignment?.data?.[0]?.name);
    } else if (routeName === "Custom Resource Student") {
      setTitle(axioResourceData?.getAxioResource?.data?.[0]?.name);
    } else {
      setTitle(course?.name ?? "Course");
    }
  }, [assignmentData, course, axioResourceData]);

  useEffect(() => {
    if (routeName === "Custom Assignment Student") {
      if (userSubmissionsData?.getUserSubmissions?.data?.[0]?.hasTeacherGraded) {
        setLabel("Graded");
      } else if (
        userSubmissionsData?.getUserSubmissions?.data?.[0] &&
        !userSubmissionsData?.getUserSubmissions?.data?.[0]?.hasTeacherGraded
      ) {
        setLabel("Submitted");
      } else {
        const dueDate = assignmentData?.getAssignment?.data?.[0]?.due_date;
        setLabel(dueDate ? `Due ${formatDate(dueDate)}` : "");
      }
    } else if (routeName === "Custom Resource Student") {
      setLabel("");
    } else {
      setLabel(course?.isDraft ? "Draft" : "Published");
    }
  }, [assignmentData, course, userSubmissionsData, routeName]);

  if (loadingUserSubmissions || loadingAssignment) {
    return (
      <View style={styles.loadingPlaceholder} role="status" accessibilityLiveRegion="polite">
        <Text style={styles.loadingText}>Loading course...</Text>
      </View>
    );
  }

  return (
    <CourseHeader
      title={title}
      label={label}
      isDraft={isDraft}
      moduleCount={moduleCount}
      onExit={handleExitPress}
      onOpenSettings={handleOpenCourseSettings}
      onNavigateToStudentView={handleNavigateToStudentView}
      onOpenGenerateDialog={handleOpenGenerateDialog}
      onTriggerPublishToggle={handleTogglePublishDialog}
      isSubmitting={isSubmitting}
      showRightSection={showRightSection}
      isEditor={isEditor}
    />
  );
};

export default CourseHeaderContainer;

const styles = StyleSheet.create({
  loadingPlaceholder: {
    height: 62,
    justifyContent: "center",
  },
  loadingText: {
    position: "absolute",
    left: -10000,
    top: -10000,
    height: 1,
    width: 1,
    overflow: "hidden",
  },
});
